import React from 'react';
import { Box, Typography, Paper, Button, Table, TableHead, TableBody, TableRow, TableCell, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useTranslation } from 'react-i18next';
import { PackingOrderDto } from '../../services/packingService';
import WmsStationHeader from '../../shared/WmsStationHeader';
import WmsPrimaryActionBar from '../../shared/WmsPrimaryActionBar';
import { WMS_TYPE_CHIP_SX } from '../../shared/wmsTypeChip';
import { formatCustomerAddress } from '../../shared/formatCustomerAddress';
import PackingChannelBanner from './PackingChannelBanner';

interface PackingOrderFoundProps {
  order: PackingOrderDto;
  source: 'product' | 'slot';
  slotCode?: string;
  onBack: () => void;
  onStartPacking: () => void;
}

const PackingOrderFound: React.FC<PackingOrderFoundProps> = ({ order, source, slotCode, onBack, onStartPacking }) => {
  const { t } = useTranslation('warehouse');
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
      <WmsStationHeader
        title={t('screens.packing.orderFound.title')}
        subtitle={t('screens.packing.orderFound.subtitle')}
      />

      <PackingChannelBanner source={order.marketplaceSource} />

      <Paper sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <InfoRow label={t('screens.packing.orderFound.order')} value={order.orderNumber} bold />
          <InfoRow label={t('screens.packing.orderFound.customer')} value={order.customerName} bold />
          <InfoRow label={t('screens.packing.orderFound.phone')} value={order.customerPhone || '-'} />
          <InfoRow label={t('screens.packing.orderFound.cargo')} value={order.shippingMethod || '-'} />
          <InfoRow
            label={t('screens.packing.orderFound.type')}
            value={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={order.orderType === 'Single' ? t('screens.packing.common.typeSingle') : t('screens.packing.common.typeMulti')}
                  size="small"
                  color={order.orderType === 'Single' ? 'info' : 'secondary'}
                  sx={WMS_TYPE_CHIP_SX}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('screens.packing.common.itemsCount', { count: order.itemCount })}
                </Typography>
              </Box>
            }
          />
          {source === 'slot' && slotCode && (
            <InfoRow label={t('screens.packing.orderFound.source')} value={t('screens.packing.orderFound.sourceValue', { code: slotCode })} />
          )}
          <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
            <InfoRow
              label={t('screens.packing.orderFound.address')}
              value={
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {formatCustomerAddress(order.customerAddress)}
                </Typography>
              }
            />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, flex: 1, overflow: 'auto' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('screens.packing.orderFound.products')}</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: { xs: 480, sm: 'auto' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32 }}></TableCell>
                <TableCell>{t('screens.packing.orderFound.colProduct')}</TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{t('screens.packing.orderFound.colQty')}</TableCell>
                <TableCell>{t('screens.packing.orderFound.colBarcode')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {order.items.map((item, idx) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ width: 32, color: 'text.secondary' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{item.productName || item.sku || '-'}</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{t('screens.packing.common.qtyPieces', { count: item.quantity })}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
                    {item.barcode || item.sku || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <WmsPrimaryActionBar
        secondaryAction={(
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onBack} size="large">
            {t('screens.packing.orderFound.back')}
          </Button>
        )}
        primaryAction={(
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={onStartPacking}
            size="large"
            color="success"
            sx={{ minWidth: 240, fontSize: 16, fontWeight: 700 }}
          >
            {t('screens.packing.orderFound.startPacking')}
          </Button>
        )}
      />
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; bold?: boolean }> = ({ label, value, bold }) => {
  // value React node olabildigi icin (orn. <Box>, <Typography>) outer'i Box yapiyoruz;
  // string ise iceride Typography sariyoruz, aksi halde dogrudan render ediyoruz —
  // boylece <p> icine <p> warning'ini onluyoruz.
  const isString = typeof value === 'string' || typeof value === 'number';
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      {isString ? (
        <Typography variant="body1" sx={{ fontWeight: bold ? 700 : 400, wordBreak: 'break-word' }}>
          {value}
        </Typography>
      ) : (
        <Box sx={{ fontWeight: bold ? 700 : 400, fontSize: '1rem', lineHeight: 1.5 }}>
          {value}
        </Box>
      )}
    </Box>
  );
};

export default PackingOrderFound;
